import React from "react";
import { Typography } from '@mui/material';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Scatter, ComposedChart, ResponsiveContainer } from 'recharts';
import { format } from "date-fns";
import PlayerComparisonGraphToolTip from "./PlayerComparisonGraphToolTip";
import "./PlayerComparisonGraph.css";

/** Neutral palette — avoids red/green good-vs-bad connotations in head-to-head views */
const PLAYER_ONE_COLOR = "#7BA4E8";
const PLAYER_TWO_COLOR = "#E8B86D";

function PlayerComparisonGraph({ graphDataOne, graphDataTwo, playerOneData, playerTwoData, compact = false }) {
    function dateFormatter(tickItem) {
        return format(new Date(tickItem), "MMM yyyy");
    }

    function getDomain(graphDataOne, graphDataTwo) {
        if ((graphDataOne != null && graphDataOne.length > 0) && (graphDataTwo != null && graphDataTwo.length > 0)) {
            const maxDate =  Math.max(graphDataOne[graphDataOne.length - 1].effective_date, graphDataTwo[graphDataTwo.length - 1].effective_date)
            const minDate =  Math.min(graphDataOne[0].effective_date, graphDataTwo[0].effective_date)

            return [maxDate, minDate]
        }
        else if (graphDataOne != null && graphDataOne.length > 0){
            return [graphDataOne[0].effective_date, graphDataOne[graphDataOne.length - 1].effective_date]
        }

        else if (graphDataTwo != null && graphDataTwo.length > 0){
            return [graphDataTwo[0].effective_date, graphDataTwo[graphDataTwo.length - 1].effective_date]
        }
        else {
            return [0, 0]
        }
    }

    const playerNameOne = playerOneData ? playerOneData.first_name + " " + playerOneData.last_name : "";
    const playerNameTwo = playerTwoData ? playerTwoData.first_name + " " + playerTwoData.last_name : "";

    const titleClass = compact ? "player-comparison-graph-title player-comparison-graph-title--compact" : "player-comparison-graph-title";
    const chartMargin = compact
        ? { top: 4, left: 4, right: 12, bottom: 0 }
        : { top: 10, left: 20, right: 50, bottom: 20 };
    const legendProps = compact
        ? { align: "center", iconSize: 8, wrapperStyle: { fontSize: "10px", paddingTop: 2 } }
        : { align: "center" };

    const compactChartHeight = 260;

    const chart = (
        <ComposedChart width={700} height={compact ? compactChartHeight : 200} data={graphDataOne} margin={chartMargin}>
            <CartesianGrid strokeDasharray="1" />
            <XAxis
                type="number"
                domain={getDomain(graphDataOne, graphDataTwo)}
                dataKey="effective_date"
                tickFormatter={dateFormatter}
                allowDuplicatedCategory={false}
                stroke="white"
                interval="preserveStartEnd"
                tickCount={compact ? 4 : 8}
                tick={{ fontSize: compact ? 10 : 12 }}
            />
            <YAxis
                reversed
                tickCount={compact ? 5 : 10}
                domain={[1, "dataMax + 10"]}
                stroke="white"
                width={compact ? 28 : 60}
                tick={{ fontSize: compact ? 10 : 12 }}
            />
            <Legend {...legendProps} />
            <Tooltip content={<PlayerComparisonGraphToolTip />} />
            {(graphDataTwo != null && graphDataTwo.length > 0) && (
                <Line
                    data={graphDataTwo}
                    dataKey="score"
                    fill={`${PLAYER_TWO_COLOR}99`}
                    name={compact ? playerNameTwo : `${playerNameTwo} - Ranking Score`}
                    stroke={PLAYER_TWO_COLOR}
                    type="monotone"
                    dot={false}
                    strokeWidth={compact ? 2 : 3}
                />
            )}
            {(graphDataOne != null && graphDataOne.length > 0) && (
                <Line
                    data={graphDataOne}
                    dataKey="score"
                    fill={PLAYER_ONE_COLOR}
                    name={compact ? playerNameOne : `${playerNameOne} - Ranking Score`}
                    stroke={PLAYER_ONE_COLOR}
                    type="monotone"
                    dot={false}
                    strokeWidth={compact ? 2 : 3}
                />
            )}
        </ComposedChart>
    );

    return (
        <div className={compact ? "player-comparison-graph--compact" : "player-comparison-graph"}>
            {!compact && (
                <Typography variant="h4" component="div" className={titleClass}>
                    Rank score comparison
                </Typography>
            )}
            {compact ? (
                <ResponsiveContainer width="100%" height={compactChartHeight} className="player-comparison-graph-chart">
                    {chart}
                </ResponsiveContainer>
            ) : (
                <ResponsiveContainer width="99%" aspect={1.6}>
                    {chart}
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default PlayerComparisonGraph;